/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment-timezone';
import { useSelector, shallowEqual } from 'react-redux';
import KebobIcon from '../../assets/images/more-horizontal.svg';
import ReplacementBadge from '../../assets/images/badge-replacement.svg';
import SLOT_STATUSES from '../../constants/slotStatuses';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../constants/ManagementCompaniesPermissions';
import IsAuthorizedForManagementCompany from '../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import IsAuthorizedForFacility from '../../components/IsAuthorizedForFacility/IsAuthorizedForFacility';
import Tooltip from '../../components/Tooltip/Tooltip';
import useOverflow from '../../hooks/useOverflow';
import { GLOBAL_CALENDAR_SHIFT_TYPES, PERMISSION } from '../../constants';
import SlotBadges from './SlotBadges';
import utils from '../../utils/utils';
import { user } from '../../redux/user';

export const SLOT_ACTIONS = {
	GO_TO_SHIFT: 'Go to shift',
	UPDATE_UNIT: 'Update unit',
	GUARANTEE: 'Guarantee',
	GUARANTEE_BUNDLE: 'Guarantee bundle',
	REOPEN_SHIFT: 'Reopen shift',
	CANCEL_SLOT: 'Cancel slot',
	ASSIGN_SHIFT: 'Assign Shift',
	ROUTE_SLOT: 'Route Slot',
	FACILITY_FLOAT: 'Facility Float',
	MARK_AS_CALLBACK: 'Mark as Callback',
	SPLIT_SHIFT: 'Split shift',
	CONFIRM: 'Confirm'
};

const pastShiftActions = [SLOT_ACTIONS.UPDATE_UNIT, SLOT_ACTIONS.SPLIT_SHIFT];

const isOnCallNotStarted = (_shift) =>
	moment.tz(_shift.start_time, _shift.facility_timezone).isSameOrAfter(moment.tz(_shift.facility_timezone));

const GlobalOverviewSlot = ({
	shift,
	slot,
	customClass = '',
	showRefreshModal,
	handleSlotAction,
	isLastDay,
	isUpcomingShift,
	globalScheduleType,
	hasUnitsEnabled,
	hasAvailableUnits,
	hasEnoughFlexFacilitiesToFloat,
	shiftsForAllDay
}) => {
	const isCancelled = slot.slot_status === SLOT_STATUSES.FACILITY_CANCELLED;
	const { ref: unitNameRef, isOverflowing } = useOverflow(slot);
	const { permissionsByFacility, facilitiesEnabled, managementCompanyPermissions } = useSelector(user, shallowEqual);

	const lowerCaseShiftType = shift.shift_type.toLowerCase();
	const shiftTypeBadgeLabel = shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND ? 'ShiftMed' : 'Flex';
	const isGSTypeAll = globalScheduleType === GLOBAL_CALENDAR_SHIFT_TYPES.ALL;

	const unit = useMemo(() => {
		if (!slot.unit_name && !slot.has_segments) return '';
		if (slot.has_segments) {
			return (
				<div className="slot-title__unit_name" ref={unitNameRef}>
					Unit: <span className="slot-title__segments_unit_title">See Split details</span>
				</div>
			);
		}
		return isOverflowing ? (
			<Tooltip label={`${slot.unit_name}`} placement={isLastDay ? 'left' : 'right'}>
				<div className="slot-title__unit_name" ref={unitNameRef}>
					Unit: <span className="bold">{slot.unit_name}</span>
				</div>
			</Tooltip>
		) : (
			<div className={classNames('slot-title__unit_name', { cancelled: isCancelled })} ref={unitNameRef}>
				Unit: <span className="bold">{slot.unit_name}</span>
			</div>
		);
	}, [slot, isOverflowing]);

	const title = useMemo(() => {
		const highestLicenseText = slot.caregiver_highest_ranked_licence
			? `, ${slot.caregiver_highest_ranked_licence}`
			: '';
		if (isCancelled) {
			return slot.caregiver_name ? (
				<div className="slot-title">
					<span className="slot-title__cancelled-name">{`${slot.caregiver_name}${highestLicenseText}`}</span>
					{(slot.unit_name || slot.has_segments) && unit}
					<span className="slot-title__cancelled-title">Cancelled</span>
				</div>
			) : (
				<div className="slot-title__cancelled-title">
					Cancelled
					{isGSTypeAll && <div className={classNames('slot-box__badge', [`slot-box__badge-${lowerCaseShiftType}`])} />}
				</div>
			);
		}

		return (
			<div className="slot-container">
				<div className="slot-header">
					{slot.caregiver_replaced_name && (
						<div className="replacement-badge-container">
							<Tooltip label={`Replaced ${slot.caregiver_replaced_name}`} placement="top">
								<img src={ReplacementBadge} alt="Replacement" />
							</Tooltip>
						</div>
					)}
					<div className={classNames('slot-title', { 'with-replacement': !!slot.caregiver_replaced_name })}>
						{slot.caregiver_name ? (
							`${slot.caregiver_name}${highestLicenseText}`
						) : (
							<>
								Open
								{isGSTypeAll && [
									<Tooltip label={shiftTypeBadgeLabel} placement="top" show={500} customClass="gs-slot-tooltip" key="i">
										<div className={`slot-box__badge slot-box__badge--${lowerCaseShiftType}`} />
									</Tooltip>,
									!!slot.is_hybrid /* hybrid shift_type is always ON_DEMAND */ && (
										<Tooltip label="Flex" placement="top" show={500} customClass="gs-slot-tooltip" key="ii">
											<div className="slot-box__badge slot-box__badge--flex" />
										</Tooltip>
									)
								]}
							</>
						)}
					</div>
				</div>
				{(slot.unit_name || slot.has_segments) && unit}
			</div>
		);
	}, [slot, unit]);

	const canCreatePastShifts = useMemo(() => {
		return (
			shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX &&
			utils.isAuthorizedExact(
				MANAGEMENT_COMPANIES_PERMISSIONS.PORTAL_CREATE_FLEX_PAST_SHIFTS,
				(managementCompanyPermissions && managementCompanyPermissions[slot.management_company_id]) || []
			)
		);
	}, [managementCompanyPermissions, slot.management_company_id, shift.shift_type]);

	const allowedPastShiftActions = useMemo(() => {
		if (canCreatePastShifts) {
			return [...pastShiftActions, SLOT_ACTIONS.CANCEL_SLOT, SLOT_ACTIONS.ASSIGN_SHIFT];
		}
		return pastShiftActions;
	}, [canCreatePastShifts]);

	const handleClick = (event, action) => {
		const now = moment.utc();
		if (!allowedPastShiftActions.includes(action) && moment.utc(shift.end_time).isBefore(now)) {
			event.preventDefault();
			showRefreshModal(true);
			return;
		}
		handleSlotAction(action, shift, slot, shiftsForAllDay);
	};

	const canAssignUnits = useMemo(() => {
		return utils.isAuthorizedExact(
			shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
			permissionsByFacility[shift.facility_id] || []
		);
	}, [permissionsByFacility, shift.facility_id, shift.shift_type]);

	const canGuaranteeSlot = useMemo(() => {
		const now = moment.utc();
		const isPastShift = slot.contract_id
			? moment.utc(shift.start_time).isBefore(now)
			: moment.utc(shift.end_time).isBefore(now);
		return (
			!isPastShift &&
			shift.shift_type !== GLOBAL_CALENDAR_SHIFT_TYPES.FLEX &&
			!shift.is_orientation &&
			!isCancelled &&
			!slot.caregiver_id
		);
	}, [shift.shift_type, slot.is_orientation, slot.caregiver_id, isCancelled]);

	const canAssignCaregiver = useMemo(() => {
		return utils.isAuthorizedExact(PERMISSION.FLEX_ADD_CAREGIVER, permissionsByFacility[shift.facility_id] || []);
	}, [permissionsByFacility, shift.facility_id]);

	const canCancelFlex = useMemo(() => {
		return utils.isAuthorizedExact(PERMISSION.FLEX_CANCEL, permissionsByFacility[shift.facility_id] || []);
	}, [permissionsByFacility, shift.facility_id]);

	const canShowPastShiftMenu = useMemo(() => {
		if (!canCreatePastShifts) return false;
		const assignVisible =
			canAssignCaregiver &&
			!slot.caregiver_id &&
			(shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX || slot.is_hybrid);
		const unitVisible = canAssignUnits && hasUnitsEnabled && hasAvailableUnits;
		return assignVisible || unitVisible || canCancelFlex;
	}, [
		canCreatePastShifts,
		canAssignCaregiver,
		slot.caregiver_id,
		shift.shift_type,
		slot.is_hybrid,
		canAssignUnits,
		hasUnitsEnabled,
		hasAvailableUnits,
		canCancelFlex
	]);

	const canConfirmSlot =
		isUpcomingShift && shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND && !!slot.can_confirm && !isCancelled;

	const showActionsForPastShift = useMemo(() => {
		if (isUpcomingShift || isCancelled) {
			return false;
		}

		if (!hasUnitsEnabled || !hasAvailableUnits || !canAssignUnits) {
			return false;
		}

		if (shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND && (slot.is_approved || slot.is_disputed)) {
			return false;
		}

		return slot.caregiver_id !== null;
	}, [
		isUpcomingShift,
		shift.shift_type,
		slot.is_approved,
		slot.caregiver_id,
		canAssignUnits,
		hasAvailableUnits,
		hasUnitsEnabled,
		isCancelled,
		slot.is_disputed
	]);

	const hasFlexAddEditPermission = useMemo(
		() => utils.isAuthorizedExact(PERMISSION.FLEX_ADD_EDIT_SHIFT, permissionsByFacility[shift.facility_id] || []),
		[permissionsByFacility, shift.facility_id]
	);

	const allowSplitShifts = useMemo(() => {
		const matchingFacility = facilitiesEnabled.find(
			(facility) => facility.management_company_id === slot.management_company_id
		);
		const { allow_split_shifts: allowSplit } = matchingFacility || {};
		return !!allowSplit;
	}, [facilitiesEnabled, slot.management_company_id]);

	const canSplitShift =
		shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX &&
		!!slot.caregiver_id &&
		allowSplitShifts &&
		hasFlexAddEditPermission;

	const showOnCallGoToShift = useMemo(() => {
		if (shift.shift_type !== GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) {
			return false;
		}

		if (slot.is_on_call === true) {
			const callbackSlot = shift.slots.find((ss) => ss.on_call_slot_id === slot.slot_id);
			if (!callbackSlot) {
				return true;
			}

			return !callbackSlot.checkout_time;
		}

		if (slot.on_call_slot_id) {
			return !slot.checkout_time;
		}

		return false;
	}, [slot, shift]);

	return (
		<div className={classNames('slot-box', customClass)}>
			{slot.caregiver_name &&
				(isGSTypeAll ? (
					<Tooltip label={shiftTypeBadgeLabel} placement="top" show={500} customClass="gs-slot-tooltip">
						<div className={classNames('slot-pillar', [`slot-pillar__${lowerCaseShiftType}`])} />
					</Tooltip>
				) : (
					<div className="slot-pillar" />
				))}
			<div className="flex-one d-flex flex-column">
				<div className="slot-box__header">
					<div className="slot-label flex-grow-1">{title}</div>
					<div className="action-menu-container">
						{isUpcomingShift && isCancelled && shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX && (
							<IsAuthorizedForManagementCompany
								exactPermission={MANAGEMENT_COMPANIES_PERMISSIONS.REOPEN_CANCELLED_SLOT}
								managementCompanyId={slot.management_company_id}
								alternativeRender={<div className="action-menu-placeholder" />}
							>
								<Dropdown>
									<Dropdown.Toggle className="action-menu">
										<img src={KebobIcon} alt="Show More" className="action-menu-icon" />
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item
											className="action-menu-item"
											onClick={(event) => handleClick(event, SLOT_ACTIONS.REOPEN_SHIFT)}
										>
											Reopen shift
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							</IsAuthorizedForManagementCompany>
						)}
						{!isCancelled && (isUpcomingShift || showActionsForPastShift || canShowPastShiftMenu) && (
							<Dropdown>
								<Dropdown.Toggle className="action-menu">
									<img src={KebobIcon} alt="Show More" className="action-menu-icon" />
								</Dropdown.Toggle>
								<Dropdown.Menu>
									{isUpcomingShift &&
										shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX &&
										!slot.caregiver_id &&
										moment.utc(shift.start_time).isAfter(moment.utc()) &&
										!!slot.enable_shift_routing && (
											<IsAuthorizedForFacility
												exactPermission={PERMISSION.PORTAL_EDIT_SHIFT_ROUTING}
												facilityId={shift.facility_id}
											>
												<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.ROUTE_SLOT)}
												>
													Modify route
												</Dropdown.Item>
											</IsAuthorizedForFacility>
										)}
									{canConfirmSlot && (
										<IsAuthorizedForFacility exactPermission={PERMISSION.EDIT} facilityId={shift.facility_id}>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => handleClick(event, SLOT_ACTIONS.CONFIRM)}
											>
												Confirm
											</Dropdown.Item>
										</IsAuthorizedForFacility>
									)}
									{hasUnitsEnabled && hasAvailableUnits && (
										<IsAuthorizedForFacility
											exactPermission={
												shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX
													? PERMISSION.FLEX_ASSIGN_UNITS
													: PERMISSION.EDIT_ASSIGNMENT
											}
											facilityId={shift.facility_id}
										>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => {
													handleClick(event, SLOT_ACTIONS.UPDATE_UNIT);
												}}
											>
												{slot.unit_id ? ' Update unit' : 'Add unit'}
											</Dropdown.Item>
										</IsAuthorizedForFacility>
									)}
									{canGuaranteeSlot && !slot.contract_id && (
										<IsAuthorizedForFacility exactPermission={PERMISSION.GUARANTEE} facilityId={shift.facility_id}>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => {
													handleClick(event, SLOT_ACTIONS.GUARANTEE);
												}}
											>
												{!slot.is_guaranteed ? 'Guarantee' : 'Remove Guarantee'}
											</Dropdown.Item>
										</IsAuthorizedForFacility>
									)}
									{canGuaranteeSlot && slot.contract_id && (
										<IsAuthorizedForFacility exactPermission={PERMISSION.GUARANTEE} facilityId={shift.facility_id}>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => {
													handleClick(event, SLOT_ACTIONS.GUARANTEE_BUNDLE);
												}}
											>
												Bundle Guarantee
											</Dropdown.Item>
										</IsAuthorizedForFacility>
									)}
									{(isUpcomingShift || canCreatePastShifts) &&
										!slot.caregiver_id &&
										(shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX || slot.is_hybrid) && (
											<IsAuthorizedForFacility
												exactPermission={PERMISSION.FLEX_ADD_CAREGIVER}
												facilityId={shift.facility_id}
											>
												<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => {
														handleClick(event, SLOT_ACTIONS.ASSIGN_SHIFT);
													}}
												>
													Assign professional
												</Dropdown.Item>
											</IsAuthorizedForFacility>
										)}
									{isUpcomingShift &&
										!!slot.caregiver_id &&
										shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX &&
										hasEnoughFlexFacilitiesToFloat &&
										!!slot.allow_flex_floating &&
										!slot.is_orientation && (
											<IsAuthorizedForManagementCompany
												exactPermission={MANAGEMENT_COMPANIES_PERMISSIONS.FACILITY_FLEX_FLOAT}
												managementCompanyId={slot.management_company_id}
												alternativeRender={<div className="action-menu-placeholder" />}
											>
												<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.FACILITY_FLOAT)}
												>
													Facility float
												</Dropdown.Item>
											</IsAuthorizedForManagementCompany>
										)}
									{isUpcomingShift &&
										!!slot.caregiver_id &&
										shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND &&
										!!shift.is_on_call &&
										!slot.marked_as_callback &&
										!slot.on_call_slot_id && (
											<IsAuthorizedForFacility
												exactPermission={PERMISSION.ON_CALL_SHIFT_MARK_AS_CALLBACK}
												facilityId={shift.facility_id}
											>
												<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.MARK_AS_CALLBACK)}
												>
													Mark as Called Back
												</Dropdown.Item>
											</IsAuthorizedForFacility>
										)}
									{/* slot is either not on demand or on demand, but not on call or callback */}
									{isUpcomingShift &&
										(shift.shift_type !== GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND ||
											(!slot.is_on_call && !slot.on_call_slot_id)) && (
											<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.GO_TO_SHIFT)}
												>
													Go to shift
												</Dropdown.Item>
										)}
									{/* slot is on call or callback without checkout time */}
									{isUpcomingShift &&
										shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND &&
										showOnCallGoToShift && (
											<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.GO_TO_SHIFT)}
												>
													Go to shift
												</Dropdown.Item>
										)}
									{(isUpcomingShift || canCreatePastShifts) && shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX && (
										<IsAuthorizedForFacility exactPermission={PERMISSION.FLEX_CANCEL} facilityId={shift.facility_id}>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => handleClick(event, SLOT_ACTIONS.CANCEL_SLOT)}
											>
												Cancel
											</Dropdown.Item>
										</IsAuthorizedForFacility>
									)}
									{canSplitShift && (
										<IsAuthorizedForManagementCompany
											exactPermission={MANAGEMENT_COMPANIES_PERMISSIONS.FLEX_SPLIT_SHIFT}
											managementCompanyId={slot.management_company_id}
											alternativeRender={<div className="action-menu-placeholder" />}
										>
											<Dropdown.Item
												className="action-menu-item"
												onClick={(event) => handleClick(event, SLOT_ACTIONS.SPLIT_SHIFT)}
											>
												Split shift
											</Dropdown.Item>
										</IsAuthorizedForManagementCompany>
									)}
									{isUpcomingShift &&
										shift.shift_type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND &&
										(!shift.is_on_call || isOnCallNotStarted(shift)) && (
											<IsAuthorizedForFacility exactPermission={PERMISSION.CANCEL} facilityId={shift.facility_id}>
												<Dropdown.Item
													className="action-menu-item"
													onClick={(event) => handleClick(event, SLOT_ACTIONS.CANCEL_SLOT)}
												>
													Cancel
												</Dropdown.Item>
											</IsAuthorizedForFacility>
										)}
								</Dropdown.Menu>
							</Dropdown>
						)}
					</div>
				</div>

				<SlotBadges slot={slot} shift={shift} />
			</div>
		</div>
	);
};

export default memo(GlobalOverviewSlot);
